import React, { useEffect, useRef, useState } from "react";

/**
 * AnimatedCounter — counts up from 0 to `target` when it enters the viewport.
 *
 * Props:
 *  - target: number to count up to (e.g. 100, 8000, 20, 5)
 *  - decimals: number of decimal places (e.g. 1 for "5.0")
 *  - duration: ms to complete the count (default 1500)
 *  - prefix: string prepended to number (e.g. "$")
 *  - suffix: string appended to number (e.g. "+" or "★")
 *  - formatter: (n) => string to override default number formatting
 */
export default function AnimatedCounter({
  target,
  decimals = 0,
  duration = 1600,
  prefix = "",
  suffix = "",
  formatter,
  className = "",
}) {
  const [value, setValue] = useState(0);
  const nodeRef = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    const node = nodeRef.current;
    if (!node) return;

    const start = () => {
      if (startedRef.current) return;
      startedRef.current = true;
      const t0 = performance.now();
      const tick = (now) => {
        const p = Math.min(1, (now - t0) / duration);
        // Ease-out cubic for a natural "arriving" feel
        const eased = 1 - Math.pow(1 - p, 3);
        setValue(target * eased);
        if (p < 1) requestAnimationFrame(tick);
        else setValue(target);
      };
      requestAnimationFrame(tick);
    };

    if (typeof IntersectionObserver === "undefined") {
      // Fallback for older browsers — start immediately
      start();
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            start();
            io.disconnect();
          }
        });
      },
      { threshold: 0.25 }
    );
    io.observe(node);
    return () => io.disconnect();
  }, [target, duration]);

  const display = (() => {
    if (formatter) return formatter(value);
    const rounded = decimals ? value.toFixed(decimals) : Math.round(value).toLocaleString();
    return `${prefix}${rounded}${suffix}`;
  })();

  return (
    <span ref={nodeRef} className={className} data-testid="animated-counter">
      {display}
    </span>
  );
}
